import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, MessageCircle, CheckCircle, ArrowRight, Loader, Navigation, ExternalLink } from "lucide-react";
import { phoneNumbers } from "../data/mock";
import useCampuses from "../hooks/useCampuses";
import SEO from "../components/SEO";

const CRM = process.env.REACT_APP_CRM_BACKEND;
const CRM_KEY = process.env.REACT_APP_CRM_API_KEY;

const Contact = () => {
  const campuses = useCampuses();
  const mainCampus = campuses.find((c) => c.featured) || campuses[0] || {};
  const [form, setForm] = useState({ name: "", childName: "", email: "", phone: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${CRM}/api/public/leads`, {
        parent_name: form.name,
        child_name: form.childName || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        message: `[${form.subject || "Contact Form"}] ${form.message}`,
        source: "Website",
        city: "Bengaluru",
        school: "TIPS Bengaluru",
      }, { headers: { "X-API-Key": CRM_KEY } });
      setSubmitted(true);
    } catch (err) {
      setError("Failed to send. Please call us at 7619305034.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <SEO
        title="Contact Us | Phone, Address & Campus Directions"
        description="Contact TIPS Bengaluru – find phone numbers, addresses and directions for all 6 campuses. Call 7619305034 or email infobengaluru@tipsmail.net."
        keywords="contact TIPS Bengaluru, TIPS school phone number, TIPS school address Bangalore, CBSE school contact Bangalore, school directions Bangalore"
        canonical="/contact"
      />
      {/* Page Header */}
      <section className="bg-[#1A0505] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-[#C83C4A] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Contact</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Contact Us</h1>
          <p className="text-gray-400 mt-3">We'd love to hear from you. Reach out to any of our campuses.</p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Phone, title: "Call Us", content: mainCampus.phone || "7619305034", sub: "Mon – Sat, 8 AM – 5 PM", action: `tel:${mainCampus.phone || "7619305034"}`, label: "Call Now" },
              { icon: Mail, title: "Email Us", content: "infobengaluru@tipsmail.net", sub: "We reply within 24 hours", action: "mailto:infobengaluru@tipsmail.net", label: "Send Email" },
              { icon: MapPin, title: "Main Campus", content: mainCampus.area || "HBR Layout, Nagawara", sub: mainCampus.address ? mainCampus.address.split(",").slice(-1)[0].trim() : "Bengaluru – 560045", action: mainCampus.mapLink || "https://maps.google.com/?q=TIPS+Bengaluru+Nagawara", label: "Get Directions" },
              { icon: MessageCircle, title: "WhatsApp", content: mainCampus.whatsapp || mainCampus.phone || "7619305034", sub: "Quick queries on WhatsApp", action: `https://wa.me/91${(mainCampus.whatsapp || mainCampus.phone || "7619305034").replace(/\D/g,"")}?text=Hi!%20I%20have%20a%20query%20about%20TIPS%20Bengaluru.`, label: "Chat Now" }
            ].map(({ icon: Icon, title, content, sub, action, label }) => (
              <a key={title} href={action} target={action.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                className="bg-gray-50 border border-gray-100 rounded-2xl p-6 hover:border-[#F8C5C9] hover:shadow-md transition-all group">
                <div className="w-12 h-12 bg-[#FEE7E9] rounded-xl flex items-center justify-center mb-4">
                  <Icon size={22} className="text-[#9B1522]" />
                </div>
                <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
                <p className="text-sm text-gray-700 font-medium mb-0.5">{content}</p>
                <p className="text-xs text-gray-500 mb-3">{sub}</p>
                <span className="text-xs font-semibold text-[#B41C2C] group-hover:text-[#9B1522] flex items-center gap-1">
                  {label} <ArrowRight size={11} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Map — all campuses in Bengaluru */}
      <section className="bg-white pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-5">
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Our Locations</span>
            <h2 className="mt-1 text-2xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Find Us Across Bengaluru
            </h2>
            <p className="text-gray-500 text-sm mt-1">6 campuses spread across the city — always close to home.</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: "400px" }}>
            <iframe
              src="https://maps.google.com/maps?q=The+Indian+Public+School+Bengaluru&t=m&z=12&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="TIPS Bengaluru Campuses Map"
              data-testid="campuses-map"
            />
          </div>
          <div className="mt-3 text-right">
            <a
              href="https://maps.google.com/maps?q=The+Indian+Public+School+Bengaluru"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B41C2C] hover:text-[#9B1522] transition-colors"
            >
              <ExternalLink size={14} /> Open in Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* Campus Cards Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">All Campuses</span>
            <h2 className="mt-1 text-2xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Campus Directory
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {campuses.map((campus) => (
              <div
                key={campus.id}
                className={`bg-white rounded-xl border-2 p-5 hover:shadow-lg transition-all group ${
                  campus.featured ? "border-[#C83C4A]" : "border-gray-100 hover:border-[#F8C5C9]"
                }`}
                data-testid="campus-contact-card"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-[#9B1522] rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                    {(campus.code || campus.name || "T").slice(0, 2).toUpperCase()}
                  </div>
                  {campus.featured && (
                    <span className="text-xs font-bold bg-[#FDF5F6] text-[#9B1522] border border-[#F8C5C9] px-2 py-0.5 rounded-full">
                      Flagship
                    </span>
                  )}
                </div>
                <h4 className="font-bold text-gray-900 mb-0.5 group-hover:text-[#9B1522] transition-colors">{campus.name}</h4>
                <p className="text-[#B41C2C] text-xs font-semibold mb-2">{campus.area}</p>
                <div className="flex items-start gap-2 mb-3">
                  <MapPin size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-500 leading-relaxed">{campus.address}</p>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <a
                    href={`tel:${campus.phone}`}
                    className="flex items-center gap-1.5 text-sm font-semibold text-[#9B1522] hover:text-[#87111E] transition-colors"
                    data-testid="campus-phone-link"
                  >
                    <Phone size={13} /> {campus.phone}
                  </a>
                  <a
                    href={campus.mapLink || `https://maps.google.com/?q=${encodeURIComponent(campus.name + " Bengaluru")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-[#B41C2C] transition-colors"
                    data-testid="campus-directions-link"
                  >
                    <Navigation size={12} /> Directions
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form + Phone Directory */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2 space-y-8">
              {/* Address */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>Main Campus Address</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="text-[#B41C2C] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600"># 78/4/1, Vidya Nidhi, Opposite Manyata Tech Park Gate-1,<br />HBR Layout, Nagawara, Bengaluru – 560045</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock size={16} className="text-[#B41C2C] flex-shrink-0" />
                    <p className="text-sm text-gray-600">Monday to Saturday, 08:00 – 17:00</p>
                  </div>
                </div>
              </div>

              {/* Phone Directory */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>Phone Directory</h3>
                <div className="space-y-2">
                  {phoneNumbers.map((pn) => (
                    <div key={pn.number} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-100">
                      <span className="text-sm text-gray-600">{pn.label}</span>
                      <a href={`tel:${pn.number}`} className="text-sm font-semibold text-[#9B1522] hover:text-[#87111E]">{pn.number}</a>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-gray-50 rounded-2xl border border-gray-100 p-8">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Send Us a Message</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Parent / Guardian Name *</label>
                        <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="contact-parent-name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Child's Name</label>
                        <input type="text" value={form.childName} onChange={(e) => setForm({ ...form, childName: e.target.value })} placeholder="Child's full name" data-testid="contact-child-name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                        <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit number" data-testid="contact-phone" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address</label>
                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" data-testid="contact-email" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject</label>
                      <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="What is this about?" data-testid="contact-subject" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Message *</label>
                      <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message here..." data-testid="contact-message" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C] resize-none" />
                    </div>
                    <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-[#9B1522] hover:bg-[#87111E] text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-70 flex items-center justify-center gap-2">
                      {loading ? <><Loader size={16} className="animate-spin" /> Sending...</> : "Send Message"}
                    </button>
                    {error && <p className="text-xs text-red-600 text-center mt-1">{error}</p>}
                  </form>
                ) : (
                  <div className="py-10 text-center" data-testid="contact-success">
                    <div className="w-16 h-16 bg-[#FEE7E9] rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-[#B41C2C]" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Message Sent!</h4>
                    <p className="text-gray-500 mb-5">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                    <button onClick={() => { setSubmitted(false); setForm({ name: "", childName: "", email: "", phone: "", subject: "", message: "" }); }} className="text-sm text-[#B41C2C] font-semibold hover:text-[#9B1522]">Send another message</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
